import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Loader2, Package } from 'lucide-react'
import { ProductEditor } from '@/components/admin/product-editor'
import { supabase } from '@/lib/supabase'
import { useState, useEffect } from 'react'
import type { Product } from '@/types/database'

export function AdminProductEditPage() {
  const { id } = useParams<{ id: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!id) return

    async function fetchProduct() {
      setIsLoading(true)
      // Use maybeSingle() to return null instead of 406 when product not found
      const { data } = await supabase
        .from('products')
        .select('*')
        .eq('id', id!)
        .maybeSingle()

      setProduct(data as Product | null)
      setIsLoading(false)
    }

    fetchProduct()
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!product) {
    return (
      <div>
        <Link to="/admin/products" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Link>
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Product not found</h3>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Edit {product.title} | Admin</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        <Link to="/admin/products" className="inline-flex items-center text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Link>

        <div>
          <h1 className="text-2xl font-bold">Edit Product</h1>
          <p className="text-muted-foreground mt-1">
            Update {product.title}
          </p>
        </div>

        <ProductEditor product={product} mode="edit" />
      </div>
    </>
  )
}
